import type { PropertyPublic, WorldManifest } from "@townsy/contracts";
import { DISTRICT_STREETS, WORLD_MANIFEST } from "@townsy/world";
import { isPlotForSale } from "./propertyLabels";

/**
 * Directory filters for the bigger city (ADR-0008 world v3: 30 stores, 3 Townsy shops and 40 home plots
 * on six streets; ADR-0015 world v4: 63 stores, Market Street with the four attractions, 110 places;
 * ADR-0041 world v5: Riverside and Hillcrest, 40 places each, 190 places in three districts).
 * Pure, so the rules are unit-tested: which district and street a place is on, which places can be
 * bought right now, a geographic order (district by district, each street from one end to the other)
 * and the filter itself.
 */

export type DirectoryType = "all" | "COMMERCIAL" | "RESIDENTIAL";

export interface DirectoryFilter {
  /** Free text (business, description, website host, address, district, owner...). */
  query: string;
  type: DirectoryType;
  /** World v5: a district id ("central", "riverside", "hillcrest"); null = every district ("All"). */
  district: string | null;
  /** A street name from `streetOptions` (e.g. "Main Street", "Elm Lane"); null = every street. */
  street: string | null;
  /** Only places a visitor can buy now: unowned stores and lots for sale. */
  availableOnly: boolean;
}

export const EMPTY_DIRECTORY_FILTER: DirectoryFilter = { query: "", type: "all", district: null, street: null, availableOnly: false };

// ---------------------------------------------------------------------------------------------
// Districts (world v5, ADR-0041 decision 9)

export interface DistrictOption {
  id: string;
  name: string;
}

/** The town's districts in manifest order (Central first, then Riverside and Hillcrest). */
export const DIRECTORY_DISTRICTS: readonly DistrictOption[] = WORLD_MANIFEST.districts.map((d) => ({ id: d.id, name: d.name }));

const DISTRICT_RANK: ReadonlyMap<string, number> = new Map(DIRECTORY_DISTRICTS.map((d, i) => [d.id, i]));
const DISTRICT_NAME: ReadonlyMap<string, string> = new Map(DIRECTORY_DISTRICTS.map((d) => [d.id, d.name]));

/** "riverside" -> "Riverside" (from the manifest); an unknown id is shown as it is, null as "". */
export function districtName(id: string | null | undefined): string {
  if (!id) return "";
  return DISTRICT_NAME.get(id) ?? id;
}

function districtRank(id: string | null | undefined): number {
  return (id ? DISTRICT_RANK.get(id) : undefined) ?? DISTRICT_RANK.size;
}

/** Every district with how many of `items` are in it, in manifest order (districts without places included, count 0). */
export function districtOptions(items: readonly Pick<PropertyPublic, "districtId">[]): (DistrictOption & { count: number })[] {
  const counts = new Map<string, number>();
  for (const p of items) counts.set(p.districtId, (counts.get(p.districtId) ?? 0) + 1);
  const known = DIRECTORY_DISTRICTS.map((d) => ({ ...d, count: counts.get(d.id) ?? 0 }));
  // A district the manifest does not know (never in v5) still gets a choice rather than hiding its places.
  const unknown = [...counts.keys()].filter((id) => !DISTRICT_RANK.has(id)).sort();
  return [...known, ...unknown.map((id) => ({ id, name: id, count: counts.get(id) ?? 0 }))];
}

/**
 * The district a street is in: the district of the places addressed on it, else the district whose named streets
 * include it (Orchard Road has no addresses of its own), else null. Street names are unique town-wide.
 */
export function districtOfStreet(street: string, items: readonly Pick<PropertyPublic, "address" | "districtId">[] = []): string | null {
  const addressed = items.find((p) => streetOfAddress(p.address) === street);
  if (addressed) return addressed.districtId;
  if (street === "Plaza Way") return DIRECTORY_DISTRICTS[0]?.id ?? null;
  for (const [id, streets] of DISTRICT_STREETS) if (streets.some((s) => s.name === street)) return id;
  return null;
}

/**
 * The directory's count line: "190 of 190 places", "40 of 190 places in Riverside", "10 of 190 places on Alder Lane in
 * Riverside". `total` is the whole town, so the line always says how much the filters hide.
 */
export function directoryCountText(shown: number, total: number, street: string | null, district: string | null): string {
  return `${shown} of ${total} places${street ? ` on ${street}` : ""}${district ? ` in ${districtName(district)}` : ""}`;
}

/**
 * The street of an address: the house number is dropped and the west extension of Main Street
 * ("10 West Main Street") is the same road as Main Street (WORLD_STREETS).
 */
export function streetOfAddress(address: string): string {
  const name = address.trim().replace(/^\d+[a-z]?\s+/i, "").trim();
  if (!name) return "Other";
  if (/^(west|east)\s+main street$/i.test(name)) return "Main Street";
  return name;
}

/** House number of an address, or null ("110 Main Street" -> 110). */
export function houseNumber(address: string): number | null {
  const m = /^\s*(\d+)/.exec(address);
  return m ? Number(m[1]) : null;
}

/**
 * Plaza Way (the Townsy shops on the plaza) first, then each district's streets in their order (Central's v3 and v4
 * streets, then Riverside's and Hillcrest's), then anything else.
 */
const ALL_STREETS = DIRECTORY_DISTRICTS.flatMap((d) => DISTRICT_STREETS.get(d.id) ?? []);
const STREET_RANK: ReadonlyMap<string, number> = new Map(["Plaza Way", ...ALL_STREETS.map((s) => s.name)].map((name, i) => [name, i]));
/** Residential lanes of every district: their places go by house number. */
const LANES: ReadonlySet<string> = new Set(ALL_STREETS.filter((s) => s.kind === "lane").map((s) => s.name));

function streetRank(street: string): number {
  return STREET_RANK.get(street) ?? STREET_RANK.size;
}

/** A store not owned by anyone (bought through the checkout) or a home plot for sale at the Real Estate office. */
export function isAvailableToBuy(p: Pick<PropertyPublic, "type" | "status" | "ownershipIntervalId">): boolean {
  if (p.type === "COMMERCIAL") return p.status === "AVAILABLE";
  return isPlotForSale(p);
}

/**
 * Street names counted and put in street order (Plaza Way, Main Street, the lanes west to east, Orchard Road, Market
 * Street and the avenues; then River Street, Alder / Hazel Lane, Mill Road; High Street, Laurel / Rowan Lane, Quarry
 * Road; others A-Z).
 */
export function countStreets(names: Iterable<string>): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of names) counts.set(s, (counts.get(s) ?? 0) + 1);
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => streetRank(a.name) - streetRank(b.name) || a.name.localeCompare(b.name));
}

/** The streets that have places, in street order, with how many places each has. */
export function streetOptions(items: readonly Pick<PropertyPublic, "address">[]): { name: string; count: number }[] {
  return countStreets(items.map((p) => streetOfAddress(p.address)));
}

/**
 * Street order: district by district (Central, Riverside, Hillcrest: manifest order); in each, the Townsy shops first
 * (civic buildings, as before), then street by street. Along an east-west street (Main Street, whose numbers grow both
 * ways from the centre as "West Main Street" and "Main Street"; Market, River and High Street) places go from west to
 * east; on a lane, by house number. Places missing from the manifest keep their house-number order at the end of their
 * street.
 */
export function directorySort<T extends Pick<PropertyPublic, "id" | "type" | "address"> & Partial<Pick<PropertyPublic, "districtId">>>(
  items: readonly T[],
  manifest: Pick<WorldManifest, "plots"> = WORLD_MANIFEST,
): T[] {
  const plots = new Map(manifest.plots.map((p) => [p.id, { x: (p.bounds.minX + p.bounds.maxX) / 2, z: (p.bounds.minZ + p.bounds.maxZ) / 2, districtId: p.districtId }]));
  const keyed = items.map((p, i) => {
    const street = streetOfAddress(p.address);
    const c = plots.get(p.id);
    const num = houseNumber(p.address) ?? Number.MAX_SAFE_INTEGER;
    const along = LANES.has(street) ? num : c ? c.x * 1000 - c.z : Number.POSITIVE_INFINITY;
    return { p, i, district: districtRank(p.districtId ?? c?.districtId), civic: p.type === "CIVIC" ? 0 : 1, rank: streetRank(street), street, along, num };
  });
  keyed.sort(
    (a, b) =>
      a.district - b.district ||
      a.civic - b.civic ||
      a.rank - b.rank ||
      a.street.localeCompare(b.street) ||
      (a.along === b.along ? 0 : a.along < b.along ? -1 : 1) ||
      a.num - b.num ||
      a.i - b.i,
  );
  return keyed.map((k) => k.p);
}

type Searchable = Pick<PropertyPublic, "id" | "type" | "status" | "address" | "districtId" | "ownerLabel" | "ownershipIntervalId" | "placement" | "civic">;

function haystack(p: Searchable): string {
  return [
    p.placement?.businessName,
    p.placement?.shortDescription,
    p.placement?.destinationHost,
    p.civic?.name,
    p.civic?.description,
    p.address,
    districtName(p.districtId),
    p.id,
    p.ownerLabel,
    isPlotForSale(p) ? "for sale plot" : null,
    p.type === "COMMERCIAL" && p.status === "AVAILABLE" ? "available store for sale" : null,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Townsy-run buildings that belong with homes in the directory: plots, home upgrades and furniture are bought there. */
const HOME_SERVICES: ReadonlySet<string> = new Set(["real_estate_office", "home_builders", "furnishings"]);

/** The places matching every part of the filter (order kept). */
export function filterDirectory<T extends Searchable>(items: readonly T[], f: DirectoryFilter): T[] {
  const q = f.query.trim().toLowerCase();
  return items.filter((p) => {
    // The Real Estate office (and the other home services) belongs with homes and with everything; the
    // attractions (world v4) only with everything.
    if (f.type === "COMMERCIAL" && p.type !== "COMMERCIAL") return false;
    if (f.type === "RESIDENTIAL" && (p.type === "COMMERCIAL" || (p.type === "CIVIC" && !HOME_SERVICES.has(p.civic?.kind ?? "")))) return false;
    if (f.district && p.districtId !== f.district) return false;
    if (f.street && streetOfAddress(p.address) !== f.street) return false;
    if (f.availableOnly && !isAvailableToBuy(p)) return false;
    return !q || haystack(p).includes(q);
  });
}

/**
 * Search across districts: how many places match the text (and the type / availability filters) in the OTHER
 * districts while one district is chosen, so the list can offer "Search all districts" instead of an empty result.
 * 0 when no district is chosen or nothing is typed. A street filter belongs to one district, so it is dropped too.
 */
export function otherDistrictMatches<T extends Searchable>(items: readonly T[], f: DirectoryFilter): number {
  if (!f.district || !f.query.trim()) return 0;
  return filterDirectory(items, { ...f, district: null, street: null }).filter((p) => p.districtId !== f.district).length;
}
